// Contract shared with PlataformaPdv — see
// claude-workspaces/giropay/docs/terminals-feature-contract.md
// All endpoint specifics live here + the *Terminals*.service.ts files, so backend contract
// drift is a one-file-per-endpoint change.

// Client-profile boolean flag that gates the Terminais menu + route (the "D0" pattern —
// a boolean field on the merchant record, read off the `clients` store payload from
// `GET /api/clients`).
//
// Dedicated client-profile boolean for the gTapp terminal-accreditation feature, read off the
// `clients` payload (`GET /api/clients`) — same "D0" pattern as `d0`/`distribuicao`/
// `external_terminal`. Requires the upstream (ServicesV3 / client record) to expose `gtapp` and
// PlataformaPdv to pass it through. See terminals-feature-contract.md (Gating).
export const TERMINALS_CLIENT_FLAG = 'gtapp';

export const hasGtappAccess = (client?: { gtapp?: unknown } | null) =>
  client?.[TERMINALS_CLIENT_FLAG] === true;

export type AccreditationStatus =
  | 'pending'
  | 'authenticated'
  | 'active'
  | 'failed'
  | 'expired'
  | 'canceled';

// Row returned by GET /api/terminals/registrations (OTP masked).
export type TerminalAccreditation = {
  id: number;
  status: AccreditationStatus | string;
  type?: string;
  provider?: string | null;
  alias?: string | null;
  otp?: string | null;
  origin_terminal?: string | null;
  equipment_id?: number | null;
  serial_number?: string | null;
  device_model?: string | null;
  created_at?: string | null;
  accredited_at?: string | null;
};

// Returned by POST create, GET /{id}/otp and POST /{id}/otp (regenerate).
export type OtpReveal = {
  id: number;
  otp: string;
  otp_expires_at?: string | null;
};

export const STATUS_LABELS: Record<string, string> = {
  pending: 'Pendente',
  authenticated: 'Autenticado',
  active: 'Ativo',
  failed: 'Falha',
  expired: 'Expirado',
  canceled: 'Cancelado',
};

export const STATUS_COLORS: Record<
  string,
  'success' | 'warning' | 'info' | 'error' | 'default'
> = {
  pending: 'warning',
  authenticated: 'info',
  active: 'success',
  failed: 'error',
  expired: 'default',
  canceled: 'default',
};

// The "Terminais" (active) section vs the "Em credenciamento" (pending) section.
export const isActiveStatus = (status?: string) => status === 'active';

// Base URL the QR encodes — the PUBLIC pdv **frontend** landing page
// (`<pdv host>/pdv/terminal-accreditation`, served by TerminalAccreditation.screen, which
// deep-links into the gTapp app and shows a fallback). Configured per-env like the other
// REACT_APP_* URLs via `REACT_APP_PDV_ACCREDITATION_URL`; falls back to the pdv app host
// (`REACT_APP_PDV_URL`) + `/terminal-accreditation` — NOT the backend, which has no such route.
export const TERMINALS_QR_LINK_BASE =
  process.env.REACT_APP_PDV_ACCREDITATION_URL ||
  (process.env.REACT_APP_PDV_URL
    ? `${process.env.REACT_APP_PDV_URL}/terminal-accreditation`
    : `${window.location.origin}/terminal-accreditation`);

// The link the QR encodes — the mobile app parses `id` + `otp` from it and runs the claim flow.
export const buildQrPayload = (reveal: { id: number; otp: string }) => {
  const params = new URLSearchParams({
    id: String(reveal.id),
    otp: reveal.otp,
  });
  return `${TERMINALS_QR_LINK_BASE}?${params.toString()}`;
};
